/**
 * PublicDossierPage — public provenance dossier at route /v/:token.
 *
 * Route: /v/:token (PUBLIC — no RequireAuth wrapper, sibling of /sign-in
 * and /verify/:token, inside QueryClientProvider).
 *
 * Audience: a stranger who landed from a QR code or shared link. May be
 * on a phone in a car park. Mobile-first, single scrolling binder.
 *
 * The API always returns HTTP 200; the `status` field carries the verdict:
 *   verified        — full disclosure:"public" payload. Render the dossier.
 *   expired         — owner can renew. Render ExpiredPanel.
 *   notVerified     — uniform not-available. Render NotAvailablePanel.
 *   revoked/unknown — also not-available; same panel (no oracle).
 *
 * Gated by VITE_PUBLIC_DOSSIER_ENABLED flag (default false; staging: true).
 *
 * Capacitor readiness:
 *   - No firebase/* imports.
 *   - No navigator.onLine.
 *   - No localStorage/sessionStorage for sensitive data.
 *   - No Web APIs without Capacitor plugin equivalents.
 *   - Standard https:// URL via React Router param.
 */

import { Button } from '@/components/Button';
import { Pill } from '@/components/Pill';
import { SkeletonList } from '@/components/Skeleton';
import { SpecBlock, SpecRows } from '@/components/SpecBlock';
import { StampChip } from '@/components/StampChip';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import { DisputeNotice, type DisputeState } from '@/features/verify/DisputeNotice';
import { MaskedVinBadge } from '@/features/verify/MaskedVinBadge';
import { ProvenanceTimeline } from '@/features/verify/ProvenanceTimeline';
import {
  ErrorPanel,
  ExpiredPanel,
  LoadingPanel,
  NotAvailablePanel,
} from '@/features/verify/shared';
import { usePublicDossier } from '@/hooks/usePublicDossier';
import { env } from '@/lib/env';
import type { PublicDossierDocument, PublicDossierPhoto } from '@/types/api';
import { ShieldCheck } from 'lucide-react';
import { useParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Column width tokens — wider than VerifyPage's 480 per design spec.
// ---------------------------------------------------------------------------

const COLUMN_SM = 640;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateIso(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Per-marque authority phrases — Sterling-verified. Values are VERBATIM.
// Lookup is normalize-then-exact-alias; no substring/includes matching.
// ---------------------------------------------------------------------------

const FALLBACK_AUTHORITY = "a factory build record or a marque's certificate of authenticity";

/**
 * Normalized alias → Sterling-verified authority phrase.
 *
 * Traps (must fall through to FALLBACK_AUTHORITY):
 *   - "daimler"      (bare Daimler is ambiguous: British Daimler under the Jaguar
 *                     Daimler Heritage Trust vs historical Daimler-Benz)
 *   - "mercedes amg" (AMG authenticity artifact differs from the MB Data Card)
 *
 * Both are absent from this map by design.
 */
const AUTHORITY_PHRASE_MAP: Record<string, string> = {
  // Mercedes-Benz aliases
  'mercedes benz':
    "a Mercedes-Benz Data Card or the Classic Center's Manufacturer's Expert Assessment",
  mercedesbenz:
    "a Mercedes-Benz Data Card or the Classic Center's Manufacturer's Expert Assessment",
  mercedes: "a Mercedes-Benz Data Card or the Classic Center's Manufacturer's Expert Assessment",
  merc: "a Mercedes-Benz Data Card or the Classic Center's Manufacturer's Expert Assessment",
  mb: "a Mercedes-Benz Data Card or the Classic Center's Manufacturer's Expert Assessment",
  'daimler benz':
    "a Mercedes-Benz Data Card or the Classic Center's Manufacturer's Expert Assessment",
  daimlerbenz: "a Mercedes-Benz Data Card or the Classic Center's Manufacturer's Expert Assessment",
  // Porsche
  porsche: 'a Porsche Kardex or Porsche Production Specification',
  // Jaguar
  jaguar: 'a Jaguar Heritage Certificate from the Jaguar Daimler Heritage Trust',
  'jaguar cars': 'a Jaguar Heritage Certificate from the Jaguar Daimler Heritage Trust',
  // Ferrari
  ferrari: 'a Ferrari Classiche Certificate of Authenticity (the Red Book)',
};

/**
 * Normalize a make string for alias lookup.
 * Steps: trim → lowercase → collapse runs of whitespace and hyphens to a
 * single space. This maps "Mercedes-Benz", "mercedes benz", and
 * "MERCEDES-BENZ" all to "mercedes benz", while "Mercedes-AMG" maps to
 * "mercedes amg" (not present in the map → generic fallback).
 */
function normalizeMake(make: string): string {
  return make
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, ' ');
}

/**
 * Return the Sterling-verified authority phrase for a vehicle's marque.
 * Undefined / empty / unmapped makes return the generic fallback.
 */
export function authorityPhraseForMake(make?: string): string {
  if (!make) return FALLBACK_AUTHORITY;
  const normalized = normalizeMake(make);
  return AUTHORITY_PHRASE_MAP[normalized] ?? FALLBACK_AUTHORITY;
}

// ---------------------------------------------------------------------------
// PageShell — mobile-first single-column layout
// ---------------------------------------------------------------------------

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        background: T.bg,
        padding: `${SPACE.lg}px ${SPACE.md}px`,
        fontFamily: F.body,
      }}
    >
      <div
        style={{
          maxWidth: COLUMN_SM,
          margin: '0 auto',
          // At lg breakpoints the column expands — handled with a CSS custom property
          // set on a parent. Inline styles don't support @media; we accept the
          // narrower column in tests and leave the wider variant to the CSS layer.
          // This is a known acceptable limitation of the inline-styles pattern.
        }}
      >
        {children}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// (1) Brand strip
// ---------------------------------------------------------------------------

function BrandStrip() {
  return (
    <div style={{ marginBottom: SPACE.lg }}>
      <StampChip size="md" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// (2) Trust banner
// ---------------------------------------------------------------------------

function TrustBanner({ asOf }: { asOf: string }) {
  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderLeft: `3px solid ${T.green}`,
        borderRadius: RADIUS.md,
        background: T.card,
        padding: `${SPACE.md}px ${SPACE.md}px`,
        marginBottom: SPACE.md,
      }}
      role="status"
      aria-label="Verification result: sealed service record"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACE.sm,
          marginBottom: SPACE.sm,
          flexWrap: 'wrap',
        }}
      >
        <ShieldCheck size={20} color={T.green} aria-hidden="true" style={{ flexShrink: 0 }} />
        <h1
          style={{
            fontFamily: F.display,
            fontSize: 22,
            fontWeight: 600,
            color: T.text,
            margin: 0,
            textWrap: 'balance',
          }}
        >
          Sealed service record
        </h1>
        <Pill color="green" size="sm">
          Sealed
        </Pill>
      </div>
      <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, margin: 0 }}>
        Sealed by Absolute Pinks. Records as of {formatDateIso(asOf)}.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// (3) Identity card
// ---------------------------------------------------------------------------

function IdentityCard({ vehicle }: { vehicle: PublicDossierDocument['vehicle'] }) {
  const rows = [
    { label: 'Chassis', value: vehicle.chassisCode, mono: true as const },
    ...(vehicle.vinPartial !== undefined || vehicle.vinBadge !== undefined
      ? [
          {
            label: 'VIN',
            value: <MaskedVinBadge vinPartial={vehicle.vinPartial} vinBadge={vehicle.vinBadge} />,
            mono: false as const,
            align: 'center' as const,
          },
        ]
      : []),
  ];

  return (
    <SpecBlock style={{ marginBottom: SPACE.md }}>
      {/* Card header — h2 in display font */}
      <div style={{ padding: `${SPACE.md}px ${SPACE.md}px ${SPACE.sm}px` }}>
        <h2
          style={{
            fontFamily: F.display,
            fontSize: 20,
            fontWeight: 600,
            color: T.text,
            margin: 0,
            textWrap: 'balance',
          }}
        >
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h2>
      </div>
      <SpecRows rows={rows} labelCol="72px" />
    </SpecBlock>
  );
}

// ---------------------------------------------------------------------------
// (5) Photo strip
// ---------------------------------------------------------------------------

function PhotoStrip({
  photos,
  vehicleLabel,
}: {
  photos: PublicDossierPhoto[];
  vehicleLabel: string;
}) {
  if (photos.length === 0) return null;

  return (
    <div
      style={{ marginBottom: SPACE.lg }}
      aria-label={`Photo gallery for ${vehicleLabel}`}
      role="region"
    >
      {/* Mobile: horizontal scroll-snap rail */}
      <div
        style={{
          display: 'flex',
          gap: SPACE.sm,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: SPACE.xs,
          // On >=640px this is overridden in-spirit by the CSS grid approach
          // below, but since we're inline-only, we accept the scroll rail on
          // all widths — functional and accessible on both.
        }}
      >
        {photos.map((photo, idx) => {
          const figNum = String(idx + 1).padStart(2, '0');
          const isLead = idx === 0;
          return (
            <figure
              key={idx}
              style={{
                flexShrink: 0,
                scrollSnapAlign: 'start',
                margin: 0,
                width: isLead ? '80%' : '60%',
                maxWidth: isLead ? 400 : 280,
              }}
            >
              <img
                src={photo.url}
                // Backend URL pattern: /v1/verify/:token/photo/:idx
                // This satisfies Capacitor readiness — no custom scheme.
                alt={`Record ${figNum} — ${vehicleLabel}`}
                loading={isLead ? 'eager' : 'lazy'}
                style={{
                  width: '100%',
                  aspectRatio: '4 / 3',
                  objectFit: 'cover',
                  borderRadius: RADIUS.md,
                  border: `1px solid ${T.border}`,
                  display: 'block',
                }}
              />
              {/* OGILVY FLAG: "Fig. 0N" caption — editorial convention per design spec. */}
              <figcaption
                style={{
                  fontFamily: F.mono,
                  fontSize: 10,
                  color: T.textMuted,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  marginTop: SPACE.xs,
                }}
              >
                Fig. {figNum}
              </figcaption>
            </figure>
          );
        })}
      </div>
      {/* Visually hidden instruction for keyboard/AT users */}
      <p className="sr-only">Photo gallery. Scroll horizontally to view all photos.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// (7) Footer
// ---------------------------------------------------------------------------

function DossierFooter({ token, make }: { token: string; make?: string }) {
  const phrase = authorityPhraseForMake(make);
  return (
    <footer
      style={{
        marginTop: SPACE.xl,
        paddingTop: SPACE.md,
        borderTop: `1px solid ${T.border}`,
      }}
    >
      {/* "Verify independently" secondary button */}
      <div style={{ marginBottom: SPACE.md }}>
        <Button as="a" href={`/verify/${token}`} variant="secondary" size="sm">
          Check this record's seal
        </Button>
      </div>

      <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6, margin: `0 0 ${SPACE.sm}px` }}>
        &ldquo;Sealed&rdquo; means Absolute Pinks signed this record when it was submitted, and we
        have checked that signature. It proves the record hasn&rsquo;t changed since then. It does
        not prove who wrote it, or that the work was actually done.
      </p>
      <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6, margin: `0 0 ${SPACE.md}px` }}>
        This record doesn&rsquo;t replace {phrase}. It seals what the owner logged.
      </p>

      {/* Brand mark + domain */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
        <StampChip size="md" />
        <span style={{ fontFamily: F.mono, fontSize: 11, color: T.textMuted }}>
          absolutepinks.com
        </span>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Full verified dossier
// ---------------------------------------------------------------------------

function VerifiedDossier({
  token,
  asOf,
  document: doc,
}: {
  token: string;
  asOf: string;
  document: PublicDossierDocument;
}) {
  const vehicleLabel = `${doc.vehicle.year} ${doc.vehicle.make} ${doc.vehicle.model}`;

  // Map server disputeStatus to DisputeState (unknown → 'none')
  const disputeState: DisputeState =
    doc.vehicle.disputeStatus === 'contested'
      ? 'contested'
      : doc.vehicle.disputeStatus === 'under-review'
        ? 'under-review'
        : 'none';

  // Collect all photos from all entries for the generic documentary gallery.
  // Spec 011 Slice 3: before/after photos (role: 'before' | 'after') are
  // owner-submitted evidence, rendered inline in ProvenanceTimeline with an
  // explicit "not independently verified" disclaimer — they are EXCLUDED
  // here so they never appear in this Fig.-numbered gallery, which reads as
  // documentary/verified evidence. Role-less photos (pre-011 data) keep the
  // existing behavior.
  const allPhotos: PublicDossierPhoto[] = doc.entries.flatMap(
    (e) => e.photos?.filter((p) => p.role !== 'before' && p.role !== 'after') ?? [],
  );

  return (
    <>
      {/* (1) Brand strip */}
      <BrandStrip />

      {/* (2) Trust banner */}
      <TrustBanner asOf={asOf} />

      {/* (3) Identity card */}
      <IdentityCard vehicle={doc.vehicle} />

      {/* (4) Dispute notice — directly under identity when relevant */}
      {disputeState !== 'none' && (
        <div style={{ marginBottom: SPACE.md }}>
          <DisputeNotice disputeState={disputeState} />
        </div>
      )}

      {/* (5) Photo strip */}
      {allPhotos.length > 0 && (
        <div style={{ marginBottom: SPACE.lg }}>
          <PhotoStrip photos={allPhotos} vehicleLabel={vehicleLabel} />
        </div>
      )}

      {/* (6) Provenance timeline */}
      <div
        style={{
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.md,
          background: T.card,
          padding: `${SPACE.md}px ${SPACE.md}px`,
          marginBottom: SPACE.md,
        }}
      >
        <ProvenanceTimeline entries={doc.entries} />
      </div>

      {/* (7) Footer */}
      <DossierFooter token={token} make={doc.vehicle.make} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton — matches the vertical stack shape of the dossier
// ---------------------------------------------------------------------------

function DossierLoadingState() {
  return (
    <>
      <BrandStrip />
      <LoadingPanel />
      <div style={{ marginTop: SPACE.md }}>
        <SkeletonList count={3} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Inner page — consumes the hook (requires QueryClientProvider above it)
// ---------------------------------------------------------------------------

function PublicDossierPageInner() {
  const { token } = useParams<{ token: string }>();
  const { data, loading, error } = usePublicDossier(token ?? null);

  // Feature-flag guard — renders a 404-equivalent if the flag is off.
  if (!env.VITE_PUBLIC_DOSSIER_ENABLED) {
    return (
      <PageShell>
        <BrandStrip />
        <NotAvailablePanel />
      </PageShell>
    );
  }

  function renderContent(): React.ReactNode {
    if (!token) {
      return <ErrorPanel message="No dossier token in the URL." />;
    }
    if (loading) {
      return <DossierLoadingState />;
    }
    if (error) {
      return (
        <>
          <BrandStrip />
          <ErrorPanel message={error.message} />
        </>
      );
    }
    if (!data) {
      return <DossierLoadingState />;
    }

    switch (data.status) {
      case 'verified':
        return <VerifiedDossier token={token} asOf={data.asOf} document={data.document} />;
      case 'expired':
        return (
          <>
            <BrandStrip />
            <ExpiredPanel />
          </>
        );
      // notVerified, revoked, unknown — all uniform not-available (no oracle).
      // All three cases intentionally produce the SAME panel — no oracle.
      case 'notVerified':
      // falls through
      // eslint-disable-next-line no-fallthrough
      case 'revoked':
      // falls through
      // eslint-disable-next-line no-fallthrough
      case 'unknown':
        return (
          <>
            <BrandStrip />
            <NotAvailablePanel />
          </>
        );
    }
  }

  return <PageShell>{renderContent()}</PageShell>;
}

// ---------------------------------------------------------------------------
// Public export — rendered inside the app's existing QueryClientProvider.
// ---------------------------------------------------------------------------

export function PublicDossierPage() {
  return <PublicDossierPageInner />;
}
